/**
 * vetokoukku-card-availability.js
 *
 * Shows the next available installation slot on each toimipiste card on the
 * vetokoukut collection page. Fetches toimipisteet, ajanvaraus metaobjects and
 * the disabled_dates metaobject in parallel, groups bookings by location, and
 * calculates the earliest bookable slot per card.
 *
 * Lead times come from each toimipiste's aikaviive_vetokoukut metafield (+ 1),
 * matching booking-form.liquid exactly. Default fallback: 8 working days.
 *
 * Disabled dates (public holidays etc.) from the disabled_dates metaobject
 * are skipped just like the booking form does.
 *
 * Each card must have:
 *   data-toimipiste-availability="<nimi>"
 *   data-capacity="<nostinten_maara>"
 */

(function () {
  const STOREFRONT_API_KEY = 'c67e06f479969086ae1fd479cad4cbc1';

  const ALL_SLOTS = [
    { label: '08:00', start: 8 * 60,  end: 10 * 60 },
    { label: '10:00', start: 10 * 60, end: 12 * 60 },
    { label: '12:00', start: 12 * 60, end: 14 * 60 },
  ];

  function getLeadDays(location, leadTimes) {
    return leadTimes[location] ?? 6;
  }

  /** "HH:MM" → minutes from midnight */
  function parseTime(str) {
    if (!str) return null;
    const [h, m] = str.split(':').map(Number);
    return h * 60 + m;
  }

  /** Date → "YYYY-MM-DD" */
  function toDateStr(d) {
    return (
      d.getFullYear() +
      '-' + String(d.getMonth() + 1).padStart(2, '0') +
      '-' + String(d.getDate()).padStart(2, '0')
    );
  }

  /** Date → "MM-DD" (for recurring disabled date matching) */
  function toMonthDay(d) {
    return String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  /**
   * Advance date by N working days, skipping weekends and disabled dates.
   * Mirrors addWorkingDaysSkippingDisabled in booking-form.liquid.
   */
  function addWorkingDays(date, days, disabledMovable, disabledRecurring) {
    const d = new Date(date);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const dow = d.getDay();
      if (dow === 0 || dow === 6) continue;
      if (disabledMovable.includes(toDateStr(d))) continue;
      if (disabledRecurring.includes(toMonthDay(d))) continue;
      added++;
    }
    return d;
  }

  /** Returns true if a date is not a working day (weekend or disabled). */
  function isDisabled(d, disabledMovable, disabledRecurring) {
    const dow = d.getDay();
    if (dow === 0 || dow === 6) return true;
    if (disabledMovable.includes(toDateStr(d))) return true;
    if (disabledRecurring.includes(toMonthDay(d))) return true;
    return false;
  }

  /** How many existing bookings overlap with the given slot on dateStr at location. */
  function slotBookingCount(dateStr, location, slot, byLocation) {
    let count = 0;
    (byLocation[location] || []).forEach((b) => {
      if (b.paiva !== dateStr) return;
      const bs = parseTime(b.aika);
      if (bs === null) return;
      const be = b.loppu_aika ? parseTime(b.loppu_aika) : bs + 120;
      if (bs < slot.end && be > slot.start) count++;
    });
    return count;
  }

  /**
   * Find the next date+slot available for a location.
   * Returns { date: Date, time: string } or null if nothing found within 90 days.
   */
  function findNextAvailable(location, byLocation, capacity, disabledMovable, disabledRecurring, leadTimes) {
    let d = addWorkingDays(new Date(), getLeadDays(location, leadTimes), disabledMovable, disabledRecurring);

    for (let i = 0; i < 90; i++) {
      if (!isDisabled(d, disabledMovable, disabledRecurring)) {
        const dateStr = toDateStr(d);
        for (const slot of ALL_SLOTS) {
          if (slotBookingCount(dateStr, location, slot, byLocation) < capacity) {
            return { date: new Date(d), time: slot.label };
          }
        }
      }
      d.setDate(d.getDate() + 1);
    }
    return null;
  }

  /** Format a Date as a short Finnish/Swedish/English string, e.g. "ma 28.4." */
  function formatDate(d) {
    const lang = document.documentElement.lang || '';
    if (lang.startsWith('sv')) {
      const days = ['sö', 'må', 'ti', 'on', 'to', 'fr', 'lö'];
      return days[d.getDay()] + ' ' + d.getDate() + '.' + (d.getMonth() + 1) + '.';
    }
    if (lang.startsWith('en')) {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return days[d.getDay()] + ' ' + d.getDate() + '/' + (d.getMonth() + 1);
    }
    const days = ['su', 'ma', 'ti', 'ke', 'to', 'pe', 'la'];
    return days[d.getDay()] + ' ' + d.getDate() + '.' + (d.getMonth() + 1) + '.';
  }

  function nextAvailableLabel() {
    const lang = document.documentElement.lang || '';
    if (lang.startsWith('sv')) return 'Nästa lediga: ';
    if (lang.startsWith('en')) return 'Next available: ';
    return 'Seur. vapaa: ';
  }

  /** Fetch recurring and movable disabled dates from the disabled_dates metaobject. */
  async function fetchDisabledDates() {
    const query = `{
      metaobject(handle: {type: "disabled_dates", handle: "disabled-dates-list"}) {
        fields { key value }
      }
    }`;
    const res = await fetch('/api/2023-07/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_API_KEY,
      },
      body: JSON.stringify({ query }),
    });
    const { data } = await res.json();
    const fields = data?.metaobject?.fields || [];
    const get = (key) => fields.find((f) => f.key === key)?.value || '';
    const parse = (str) => str.split(',').map((s) => s.trim().replace(/[\[\]"]/g, '')).filter(Boolean);
    return {
      recurring: parse(get('recurring')),
      movable: parse(get('movable')),
    };
  }

  /** Fetch aikaviive_vetokoukut per toimipiste and return { nimi: leadDays } map. */
  async function fetchLeadTimes() {
    const query = `{
      metaobjects(type: "toimipisteet", first: 50) {
        edges {
          node { fields { key value } }
        }
      }
    }`;
    const res = await fetch('/api/2023-07/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_API_KEY,
      },
      body: JSON.stringify({ query }),
    });
    const { data } = await res.json();
    const leadTimes = {};
    (data?.metaobjects?.edges || []).forEach(({ node }) => {
      const get = (key) => node.fields.find((f) => f.key === key)?.value;
      const nimi = get('nimi');
      if (!nimi) return;
      const base = parseInt(get('aikaviive_vetokoukut') || '5', 10);
      leadTimes[nimi] = base + 1;
    });
    return leadTimes;
  }

  /** Fetch all ajanvaraus metaobjects, paginated, and group by toimipiste. */
  async function fetchAllBookings() {
    let hasNextPage = true;
    let after = null;
    const byLocation = {};

    while (hasNextPage) {
      const query = `{
        metaobjects(type: "ajanvaraus", first: 250${after ? `, after: "${after}"` : ''}) {
          edges {
            cursor
            node { fields { key value } }
          }
          pageInfo { hasNextPage }
        }
      }`;

      const res = await fetch('/api/2023-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_API_KEY,
        },
        body: JSON.stringify({ query }),
      });

      const { data } = await res.json();
      const edges = data.metaobjects.edges;

      edges.forEach(({ node }) => {
        const get = (key) => node.fields.find((f) => f.key === key)?.value;
        const location = get('toimipiste');
        if (!location) return;
        if (!byLocation[location]) byLocation[location] = [];
        byLocation[location].push({
          paiva: get('paiva'),
          aika: get('aika'),
          loppu_aika: get('loppu_aika'),
        });
      });

      hasNextPage = data.metaobjects.pageInfo.hasNextPage;
      after = edges.length > 0 ? edges[edges.length - 1].cursor : null;
    }

    return byLocation;
  }

  async function init() {
    const cards = document.querySelectorAll('[data-toimipiste-availability]');
    if (!cards.length) return;

    // Show spinner on all cards while loading
    cards.forEach((el) => {
      el.innerHTML = '<span class="card__next-available-spinner" aria-hidden="true"></span>';
    });

    let byLocation, disabled, leadTimes;
    try {
      [byLocation, disabled, leadTimes] = await Promise.all([fetchAllBookings(), fetchDisabledDates(), fetchLeadTimes()]);
    } catch (e) {
      cards.forEach((el) => (el.innerHTML = ''));
      return;
    }

    cards.forEach((el) => {
      const location = el.dataset.toimipisteAvailability;
      const capacity = parseInt(el.dataset.capacity || '1', 10);
      const result = findNextAvailable(location, byLocation, capacity, disabled.movable, disabled.recurring, leadTimes);

      if (result) {
        el.innerHTML =
          '<span class="card__next-available-text">' +
          nextAvailableLabel() +
          '<strong>' + formatDate(result.date) + '</strong>' +
          '</span>';
      } else {
        el.innerHTML = '';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
